import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark';

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

/**
 * Reads and writes the same `windocs.theme` key as the inline script in
 * index.html, which applies the class before first paint to avoid a flash.
 */
function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

function initialTheme(): Theme {
  if (typeof window === 'undefined') {
    return 'light';
  }
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: initialTheme(),

      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },

      toggleTheme: () => {
        get().setTheme(get().theme === 'dark' ? 'light' : 'dark');
      },
    }),
    {
      name: 'windocs.theme',
      /* Stored as the bare string so the pre-paint script can read it without
         parsing zustand's wrapper object. */
      storage: {
        getItem: (name) => {
          const value = localStorage.getItem(name);
          return value ? { state: { theme: value as Theme }, version: 0 } : null;
        },
        setItem: (name, value) => {
          localStorage.setItem(name, value.state.theme);
        },
        removeItem: (name) => {
          localStorage.removeItem(name);
        },
      },
    },
  ),
);
